import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  StyleSheet,
  Text,
  View,
  TextInput,
  TouchableOpacity,
  Alert,
  BackHandler,
  ActivityIndicator,
} from "react-native";
import { createOnlinePayment, completeOnlinePayment } from "@services/api";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import { colors } from "@constants/theme";
import { fontFamily } from "@utils/styles";
import { useDispatch } from "react-redux";
import { actions as configActions } from "@reducers/configReducer";
import analytics from "@services/analytics";
import CustomText from "@components/CustomText";
import { debounce } from "lodash";
import OnlinePaymentHOC from "./OnlinePaymentHOC";

function CpOnlinePayment({
  navigation,
  route,
  processPayment,
  setInitiateResult,
  setProcessResult,
}) {
  const [amount, setAmount] = useState();
  const {
    online_payment_screen_title,
    online_payment_title,
    online_payment_subtitle,
    amount_to_collect_text,
    amount_to_collect,
    error_text,
    spokeName = "",
    source = "LEDGER",
  } = route?.params?.ledger;
  const backHandler = useRef(null); // backHandler
  const paymentIdRef = useRef(null);
  const textInputRef = useRef(null);
  const btnLoadingRef = useRef(false);
  const dispatch = useDispatch();

  const showError = useMemo(() => {
    return amount && parseFloat(amount.slice(1)) > parseFloat(amount_to_collect);
  }, [amount, amount_to_collect]);

  const initiateJuspayPaymentPage = (data) => processPayment(data);

  const handleCompletePayment = async (status, response_payload) => {
    try {
      const payment_id = paymentIdRef.current;
      const payload = {
        payment_id,
        status,
        response_payload,
        source,
        spoke_name: spokeName,
      };
      const { data } = await completeOnlinePayment(payload);
      return data?.message
        ? Alert.alert("", data.message, [
            {
              text: "OK",
              onPress: () => {
                navigation.pop();
              },
            },
          ])
        : null;
    } catch (err) {
      return showPaymentFailedOrPendingAlert(
        err.response.data.message || "Something went wrong!",
      );
    }
  };

  const getPaymentProcessPayload = debounce(async () => {
    if (btnLoadingRef.current) return;
    btnLoadingRef.current = true;
    const floatAmount = parseFloat(amount.slice(1));

    if (Number.isNaN(floatAmount) || floatAmount <= 0) {
      Alert.alert("", "Please add a valid amount", [
        {
          text: "OK",
          onPress: () => {
            setAmount("₹");
          },
        },
      ]);
      btnLoadingRef.current = false;
      return;
    }

    const payload = {
      amount: floatAmount,
      source,
      spoke_name: spokeName,
    };

    analytics.trackEvent("CREATE_PAYMENT", payload);
    try {
      const { data } = await createOnlinePayment(payload);
      paymentIdRef.current = data.payment_id;
      await initiateJuspayPaymentPage(data);
    } catch (e) {
      console.log("error", e);
    } finally {
      btnLoadingRef.current = false;
    }
  }, 500);

  const showPaymentFailedOrPendingAlert = (message) => {
    Alert.alert("", message, [
      {
        text: "OK",
        onPress: () => {
          navigation.pop();
        },
      },
    ]);
  };

  const initiateResult = ({ result, event, payload }) => {
    if (payload.status === "success") {
      dispatch(
        configActions.setHyperSDKInitialized({
          hyperSDKInitialized: true,
        }),
      );
    } else {
      dispatch(
        configActions.setHyperSDKInitialized({
          hyperSDKInitialized: false,
        }),
      );
    }
  };

  const processResult = ({ result, event, payload }) => {
    const { error } = result;
    const innerPayload = result.payload || {};
    const status = innerPayload.status || "";
    console.log("error", error);
    handleCompletePayment(status, innerPayload);
  };
  const onBackPress = () => {
    return false;
  };

  // TODO only integer input
  useEffect(() => {
    setInitiateResult(() => initiateResult);
    setProcessResult(() => processResult);
    textInputRef.current.focus();
    setAmount("₹");
    backHandler.current = BackHandler.addEventListener(
      "hardwareBackPress",
      onBackPress,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle={online_payment_screen_title || ""}
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
      />
      <View style={styles.cardContainer}>
        <View style={styles.innerContainer}>
          <CustomText variant="title_16">
            {online_payment_title || ""}
          </CustomText>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "center",
            }}
          >
            <Text style={[styles.subtitle, { marginHorizontal: 4 }]}>
              {online_payment_subtitle || ""}
            </Text>
            <Text
              style={[styles.subtitle, { fontFamily: fontFamily.semiBold }]}
            >
              {`${amount_to_collect_text}`}
            </Text>
          </View>
          <TextInput
            style={styles.input}
            testID="amountTextInput"
            onChangeText={(val) => {
              // limiting to integer values only (0-9)
              // no invalid character allowed
              if (val === "₹" || val === "") return setAmount("₹");
              const regex = new RegExp("([0-9])");
              if (!regex.test(val.slice(-1))) return;
              setAmount(val);
            }}
            value={amount}
            keyboardType="numeric"
            ref={textInputRef}
            placeholder="₹"
          />
          {showError ? (
            <Text style={styles.error}>{error_text || ""}</Text>
          ) : null}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "center",
              marginVertical: 10,
            }}
          >
            <TouchableOpacity
              style={{
                backgroundColor:
                  btnLoadingRef.current || showError
                    ? colors.silver
                    : "#00AB59",
                paddingVertical: 8,
                paddingHorizontal: 12,
                flex: 1,
                alignItems: "center",
                borderRadius: 8,
              }}
              testID="createPaymentButton"
              onPress={getPaymentProcessPayload}
              disabled={btnLoadingRef.current || showError}
            >
              {btnLoadingRef.current ? (
                <ActivityIndicator size="small" color={colors.white} />
              ) : (
                <Text
                  style={{
                    color: "white",
                    fontSize: 14,
                    fontFamily: fontFamily.semiBold,
                  }}
                >
                  पेमेंट करें
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  cardContainer: {
    backgroundColor: colors.white,
    padding: 10,
    alignItems: "center",
    elevation: 2,
    margin: 16,
    borderRadius: 10,
  },
  innerContainer: {
    alignItems: "center",
  },
  subtitle: {
    fontSize: 12,
    // fontFamily: fontFamily.semiBold,
  },
  input: {
    width: 170,
    height: 75,
    color: "black",
    elevation: 1,
    padding: 15,
    fontSize: 20,
    fontFamily: fontFamily.semiBold,
    fontWeight: "bold",
  },
  error: { fontSize: 12, color: "red", marginVertical: 4 },
});

export default OnlinePaymentHOC(CpOnlinePayment);
